import { config } from "~/config";

export type ModuleImport = Record<string, () => Promise<unknown>>;
export type ModuleConfig = Record<string, any>;
export type ModuleRegistry = Record<string, WidgetModule>;
export type WidgetRegistry = Record<string, BaseWidget>;
export type WidgetIndex = Record<string, WidgetModule>;

export interface Store {
	modules: ModuleRegistry;
	widgets: WidgetRegistry;
	index: WidgetIndex;
}

export interface WebWidgetsWindow extends Window {
	YaaWebWidgets: {
		[key: string]: any;
		mount: (module: string, config?: ModuleConfig) => void;
		overrideStyle: (style: string) => void;
	};
}

/**
 * Load and register all available modules for use
 */
export async function registerModules(moduleImports: ModuleImport): Promise<Store> {
	const modules = await loadModules(moduleImports);
	const moduleConfigs = config.moduleConfig || {};
	const moduleRegistry: ModuleRegistry = {};
	const widgetRegistry: WidgetRegistry = {};
	const widgetIndex: WidgetIndex = {};

	const tasks: (Promise<void> | void)[] = [];

	// Start initalization process
	for (const module of modules) {
		module.config = moduleConfigs[module.id] || {};
		tasks.push(module.onSetup());
		moduleRegistry[module.id] = module;
	}

	await Promise.all(tasks);
	tasks.length = 0;

	// Finish initialization process
	for (const module of modules) {
		for (const widgetId in module.widgets) {
			widgetRegistry[widgetId] = module.widgets[widgetId];
			widgetIndex[widgetId] = module;
		}

		tasks.push(module.onReady());
	}

	await Promise.all(tasks);

	return {
		modules: moduleRegistry,
		widgets: widgetRegistry,
		index: widgetIndex,
	};
}

/**
 * Load all modules and delegate based on the current environment.
 */
async function loadModules(modules: ModuleImport): Promise<WidgetModule[]> {
	const promises: WidgetModule[] = [];

	for (const key in modules) {
		const module = (await modules[key]()) as { default: new (key: string) => WidgetModule };
		const instance = new module.default(key);
		await instance.onSetup();

		promises.push(instance);
	}

	return await Promise.all(promises);
}

/**
 * The base class which all widget modules extend
 */
export abstract class WidgetModule<TConfig = any> {
	public readonly id: string;
	public readonly widgets: Record<string, BaseWidget> = {};
	public readonly parentDocument: Document = document;

	public config!: TConfig;

	protected constructor(id: string) {
		this.id = id;
	}

	/**
	 * Module hook called when the module is loaded
	 */
	public abstract onSetup(): void | Promise<void>;

	/**
	 * Module hook called after all modules have loaded
	 */
	public abstract onReady(): void | Promise<void>;

	/**
	 * Register a new widget with the module
	 */
	protected registerWidget(id: string, widget: BaseWidget) {
		this.widgets[id] = widget;
	}
}

/**
 * Represents a single scoped widget registered by a module
 */
export abstract class BaseWidget<TConfig = any> {
	/**
	 * Mount this widget in the DOM and initialize it
	 */
	public abstract onMount(config: TConfig): void | Promise<void>;
}
